import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { LatLngTuple, LatLngBounds } from 'leaflet'; // Updated import
import TestComponent from './TestComponent';
import { ApiRequest } from 'GlobalFunctions';
import UsersList from './components/Users';
import ClientsList from './components/Clients';  // Add this import
import LoadingCircle from 'elements/LoadingCircle';
import { HiMapPin } from "react-icons/hi2";

import ReactDOMServer from 'react-dom/server';
import DateRange from 'elements/DateRange';
import moment from 'moment';
import { Divider, FormControlLabel, Switch } from '@mui/material';
import { HiFlag } from "react-icons/hi2";

// Color palette for user route lines
const ROUTE_LINE_COLORS = [
    '#1976d2', '#e91e63', '#9c27b0', '#ff9800', '#009688',
    '#795548', '#607d8b', '#3f51b5', '#00bcd4', '#8bc34a',
    '#ff5722', '#673ab7', '#03a9f4', '#cddc39', '#f44336'
];

// Define custom icon using React icons
const createReactIconMarker = (color = '#4285F4', isLastOrder = false) => {
    const iconHtml = ReactDOMServer.renderToString(
        <div style={{ color, fontSize: '32px' }}>
            {isLastOrder ? <HiFlag /> : <HiMapPin />}
        </div>
    );
    
    return L.divIcon({
        html: iconHtml,
        className: 'react-pin-icon',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });
};

// Component to fit bounds when markers are loaded
function MapBoundsHandler({ positions }: { positions: LatLngTuple[] }) {
    const map = useMap();
    
    useEffect(() => {
        if (positions.length > 0) {
            const bounds = L.latLngBounds(positions);
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [positions, map]);
    
    return null;
}

export default function Map() {
    const [userOrders, setUserOrders] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [validPositions, setValidPositions] = useState<LatLngTuple[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]); // Add this state
    const [showRouteLines, setShowRouteLines] = useState<boolean>(false);
    const [disabledRouteUserIds, setDisabledRouteUserIds] = useState<Set<string>>(new Set());
    const [formValues, setFormValues] = useState<any>({
        date: {
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD")
        }
    });

    const getOrders = async () => {
        setIsLoading(true);
        await ApiRequest({
            url: '/orders-map/list',
            method: 'get',
            headers: {
                'ltkn': localStorage.getItem('ltkn')
            },
            query: {
                user_ids: selectedUserIds.length > 0 ? selectedUserIds : undefined,
                client_ids: selectedClientIds.length > 0 ? selectedClientIds : undefined,
                start_date: formValues.date.startDate,
                end_date: formValues.date.endDate
            },
            setResponse: (response: any) => {
                if (response.status === 'success') {
                    setUserOrders(response.data);
                } else {
                    console.error('Error in orders map response:', response.message);
                }
                setIsLoading(false);
            }
        });
    };

    useEffect(() => {
        getOrders();
    }, [selectedUserIds, selectedClientIds, formValues.date]); // Update dependency array

    const handleUserSelection = (userIds: string[]) => {
        setSelectedUserIds(userIds);
    };

    const handleClientSelection = (clientIds: string[]) => { // Add this handler
        setSelectedClientIds(clientIds);
    };

    // Default center if no orders - properly typed as LatLngTuple
    const defaultCenter: LatLngTuple = [4.7110, -74.0721];
    
    useEffect(() => {
        // Extract valid positions for map fitting
        const positions: LatLngTuple[] = [];
        userOrders.forEach(user => {
            user.orders.forEach((order: any) => {
                if (order.latitud && order.longitud) {
                    const lat = parseFloat(order.latitud);
                    const lng = parseFloat(order.longitud);
                    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                        positions.push([lat, lng]);
                    }
                }
            });
        });
        setValidPositions(positions);
    }, [userOrders]);
    
    // Toggle individual user route line
    const toggleUserRoute = (userId: string) => {
        setDisabledRouteUserIds(prev => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    // Build polylines per user, sorted by order_date
    const routeLines = showRouteLines ? userOrders.map((user, userIndex) => {
        // Skip disabled users
        if (disabledRouteUserIds.has(String(user.user_id))) return null;

        // Sort orders by date ascending for this user
        const sortedOrders = [...user.orders]
            .filter((order: any) => {
                if (!order.latitud || !order.longitud) return false;
                const lat = parseFloat(order.latitud);
                const lng = parseFloat(order.longitud);
                return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
            })
            .sort((a: any, b: any) => new Date(a.order_date).getTime() - new Date(b.order_date).getTime());

        if (sortedOrders.length < 2) return null;

        const positions: LatLngTuple[] = sortedOrders.map((order: any) => [
            parseFloat(order.latitud),
            parseFloat(order.longitud)
        ]);

        const lineColor = ROUTE_LINE_COLORS[userIndex % ROUTE_LINE_COLORS.length];

        return (
            <Polyline
                key={`route-${user.user_id}`}
                positions={positions}
                pathOptions={{
                    color: lineColor,
                    weight: 3,
                    opacity: 0.8,
                    dashArray: '8, 6'
                }}
            />
        );
    }).filter(Boolean) : [];

    // Prepare markers for all orders from all users
    const allMarkers = userOrders.flatMap(user => 
        user.orders.map((order: any, index: number) => {
            if (order.latitud && order.longitud) {
                const lat = parseFloat(order.latitud);
                const lng = parseFloat(order.longitud);
                
                // Validate coordinates
                if (isNaN(lat) || isNaN(lng)) {
                    console.error('Invalid coordinates', { lat: order.latitud, lng: order.longitud });
                    return null;
                }
                
                // Check if coordinates are in valid range
                if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                    console.error('Coordinates out of range', { lat, lng });
                    return null;
                }
                
                const position: LatLngTuple = [lat, lng];
                
                // Determine marker color based on order_type_id
                let markerColor = '#4285F4'; 
                if (order.order_type_id === 1) {
                    markerColor = '#4CAF50'; 
                } else if (order.order_type_id === 2) {
                    markerColor = '#e7a42a';
                } else if (order.order_type_id === 3) {
                    markerColor = '#cd3131'; 
                }
                
                // Check if this is the last order
                const isLastOrder = order.last_order === true;
                
                return (
                    <Marker 
                        key={`${user.user_id}-${order.order_id || index}`} 
                        position={position}
                        icon={createReactIconMarker(markerColor, isLastOrder)}
                    >
                        <Popup>
                            <div>
                                <h3>Vendedor: {user.user_name}</h3>
                                <p>Cliente: {order.client_name} {order.client_last_name}</p>
                                <p>Empresa: {order.business_name}</p>
                                <p>Fecha: {order.order_date}</p>
                                <p>Valor: ${Number(order.valor).toLocaleString()}</p>
                                <p>
                                    <div style={{ 
                                        display: 'inline-block', 
                                        backgroundColor: 
                                            order.order_type_id === 1 ? '#4CAF50' : 
                                            order.order_type_id === 2 ? '#e7a42a' : 
                                            order.order_type_id === 3 ? '#cd3131' : '#4285F4',
                                        color: 'white',
                                        padding: '3px 10px',
                                        borderRadius: '16px',
                                        fontSize: '0.85rem',
                                        fontWeight: 500
                                    }}>
                                        {order.order_type_id === 1 ? 'Efectivo' : 
                                         order.order_type_id === 2 ? 'Cotizacion' : 
                                         order.order_type_id === 3 ? 'Visita sin compra' : 'Desconocido'}
                                    </div>
                                </p>
                                {isLastOrder && <p><strong>Última orden</strong></p>}
                            </div>
                        </Popup>
                    </Marker>
                );
            }
            return null;
        }).filter(Boolean)
    );

    return (
        <div id="map_wrapper">
           
            <div className="map-container" style={{height: '100%'}}>
            <div className="map_top_bar">
                <DateRange
                    className="filters_datePicker"
                    label="Date range"
                    variant="outlined"
                    fullWidth
                    value={{
                        startDate: formValues.date?.startDate || null,
                        endDate: formValues.date?.endDate || null,
                    }}
                    format="MMMM DD, yyyy"
                    onChange={(e: any) => {
                        if (e.startDate && e.endDate) {
                            const startDate = moment(e.startDate).format("YYYY-MM-DD");
                            const endDate = moment(e.endDate).format("YYYY-MM-DD");

                            setFormValues({
                                ...formValues,
                                date: { startDate, endDate },
                            });
                        }
                    }}
                    variantDatePicker="side-by-side"
                />
            </div>
                {isLoading ? (
                    <LoadingCircle />
                ) : (
                    <MapContainer center={defaultCenter} zoom={13} scrollWheelZoom={false} className="map-container">
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {allMarkers}
                        {routeLines}
                        {validPositions.length > 0 && <MapBoundsHandler positions={validPositions} />}
                    </MapContainer>
                )}
            </div>

            <div className="filters-wrapper">
                <UsersList onSelectUsers={handleUserSelection} />
                <Divider />
                {/* <ClientsList onSelectClients={handleClientSelection} /> */}

                <Divider />
                <div className="filters_section">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h2>Recorrido</h2>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showRouteLines}
                                    onChange={(e) => setShowRouteLines(e.target.checked)}
                                    size="small"
                                />
                            }
                            label=""
                            sx={{ mr: 0 }}
                        />
                    </div>

                {showRouteLines && userOrders.length > 0 && (
                    <div className="filters_section" style={{ padding: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                                {userOrders.map((user, idx) => {
                                    const isDisabled = disabledRouteUserIds.has(String(user.user_id));
                                    return (
                                        <div 
                                            key={user.user_id} 
                                            onClick={() => toggleUserRoute(String(user.user_id))}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px',
                                                cursor: 'pointer',
                                                opacity: isDisabled ? 0.4 : 1,
                                                transition: 'opacity 0.2s',
                                                padding: '4px 6px',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <div style={{
                                                width: '24px',
                                                height: '4px',
                                                backgroundColor: isDisabled ? '#ccc' : ROUTE_LINE_COLORS[idx % ROUTE_LINE_COLORS.length],
                                                borderRadius: '2px',
                                                flexShrink: 0
                                            }} />
                                            <span style={{ 
                                                fontSize: '0.8rem',
                                                textDecoration: isDisabled ? 'line-through' : 'none',
                                                color: isDisabled ? '#999' : 'inherit'
                                            }}>{user.user_name}</span>
                                        </div>
                                    );
                                })}
                            </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    )
}


